import { styled, TableRow } from '@mui/material';

// StyledTableRow for consistent row styling
export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
    fontFamily: 'Arial',
  },
}));

// Helper to update parent and subrow values for a given key and value
export const updateRowValue = (
  prev,
  key,
  updatedValue,
  setRowKeys = [],
  setSubRowKeys = [],
  accordionKey,
  saveKey
) => {
  let obj = {
    ...prev,
    [key]: {
      ...prev[key],
      value: updatedValue,
    },
  };
  if (setRowKeys.length > 0) {
    setRowKeys.forEach((subRowKey) => {
      obj[subRowKey] = {
        ...obj[subRowKey],
        value: updatedValue,
      };
    });
  }
  if (setSubRowKeys.length > 0) {
    setSubRowKeys.forEach((subRowKey) => {
      obj[accordionKey] = {
        ...obj[accordionKey],
        rows: obj[accordionKey]?.rows?.map((childRow) => ({
          ...childRow,
          [subRowKey]: {
            ...childRow[subRowKey],
            value: updatedValue,
          },
        })),
      };
    });
  }
  if (saveKey) {
    obj[saveKey] = {
      ...obj[saveKey],
      disabled: false,
    };
  }
  return obj;
};

// Find Changes in Row Helper
export const findRowChanges = ({ originalRow, updatedRow, rowToSave }) => {
  Object.keys(updatedRow).forEach((key) => {
    if (originalRow[key].value !== updatedRow[key].value) {
      rowToSave[key] = updatedRow[key];
    }
    //Compare nested rows for changes
    else if (updatedRow[key].rows) {
      updatedRow[key].rows.forEach((nestedRow, index) => {
        const originalNestedRow = originalRow[key].rows[index];
        const nestedChanges = Object.keys(nestedRow).filter((nestedKey) => {
          return (
            originalNestedRow[nestedKey].value !== nestedRow[nestedKey].value
          );
        });

        // Create row object for each nested change
        const nestedRowToSave = {
          lineNo: nestedRow.lineNo,
          itemId: nestedRow.itemId,
        };
        nestedChanges.forEach((nestedKey) => {
          nestedRowToSave[nestedKey] = nestedRow[nestedKey];
        });

        if (nestedChanges.length > 0) {
          rowToSave[key] = {
            ...rowToSave[key],
            rows: [...(rowToSave[key]?.rows || []), nestedRowToSave],
          };
        }
      });
    }
  });
};

// Sort helper
export const sortByProperty = (dataArray, property, order = 'asc') => {
  return dataArray.sort((a, b) => {
    let valA = a[property];
    let valB = b[property];
    const valADate = new Date(valA);
    const valBDate = new Date(valB);

    if (
      valADate instanceof Date &&
      valBDate instanceof Date &&
      !isNaN(valADate) &&
      !isNaN(valBDate) &&
      typeof valA !== 'number' &&
      typeof valB !== 'number'
    ) {
      return order === 'asc' ? valADate - valBDate : valBDate - valADate;
    }
    if (typeof valA === 'number' && typeof valB === 'number') {
      return order === 'asc' ? valA - valB : valB - valA;
    }
    if (valA?.rows && valB?.rows && valA?.stack && valB?.stack) {
      valA = valA.rows
        .map((item) => item.toLowerCase())
        .join(' ')
        .split('-')[0];
      valB = valB.rows
        .map((item) => item.toLowerCase())
        .join(' ')
        .split('-')[0];
      return order === 'asc'
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    }
    if (
      (valA?.input || valA?.type === 'date') &&
      (valB?.input || valB?.type === 'date')
    ) {
      const valADate = new Date(valA.value);
      const valBDate = new Date(valB.value);
      if (
        valADate instanceof Date &&
        valBDate instanceof Date &&
        !isNaN(valADate) &&
        !isNaN(valBDate)
      ) {
        return order === 'asc' ? valADate - valBDate : valBDate - valADate;
      }
      if (typeof valA.value === 'number' && typeof valB.value === 'number') {
        return order === 'asc'
          ? valA.value - valB.value
          : valB.value - valA.value;
      }
      if (typeof valA.value === 'string' && typeof valB.value === 'string') {
        return order === 'asc'
          ? String(valA.value).localeCompare(String(valB.value))
          : String(valB.value).localeCompare(String(valA.value));
      }
    }
    if (
      (valA?.value &&
        (valA?.dropDownSelect ||
          valA?.erpLink ||
          valA?.textWithColor ||
          valA.link) &&
        valB?.value &&
        (valB?.dropDownSelect ||
          valB?.erpLink ||
          valB?.textWithColor ||
          valB.link)) ||
      (valA?.value && valA.itemImage && valB?.value && valB?.itemImage)
    ) {
      const ValAisStringNumber = !isNaN(
        Number(valA?.value?.replace(/[%+]/g, ''))
      );
      const ValBisStringNumber = !isNaN(
        Number(valB?.value?.replace(/[%+]/g, ''))
      );

      valA = valA?.value?.replace(/[%+]/g, '');
      valB = valB?.value?.replace(/[%+]/g, '');

      return order === 'asc' && ValAisStringNumber && ValBisStringNumber
        ? Number(valA) - Number(valB)
        : order === 'desc' && ValAisStringNumber && ValBisStringNumber
        ? Number(valB) - Number(valA)
        : order === 'asc'
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    if (valA?.checkBox && valB?.checkBox) {
      const toBool = (v) => {
        if (typeof v === 'boolean') return v;
        if (typeof v === 'string')
          return v.toLowerCase() === 'true' || v === '1';
        if (typeof v === 'number') return v !== 0;
        return Boolean(v);
      };
      const aBool = toBool(valA.value);
      const bBool = toBool(valB.value);
      if (aBool === bBool) return 0;
      if (order === 'asc') {
        return aBool < bBool ? -1 : 1;
      } else {
        return aBool > bBool ? -1 : 1;
      }
    }
    return order === 'asc'
      ? String(valA?.value || valA).localeCompare(String(valB?.value || valB))
      : String(valB?.value || valB).localeCompare(String(valA?.value || valA));
  });
};
